import { type FocusEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { PAY_METHOD } from 'constants/payment';
import {
  useAmount,
  useMainNavigate,
  usePayment,
  useSelectCard,
  useTranslate,
  useValidate,
} from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useGetCertQuery } from 'store/payment/service';
import { ButtonKinds } from 'types/button';
import { ecryptedWithJwkPublickKey, getOfferHrefWithVersion } from 'utils';
import { SelectCard } from 'views/components/fonbet/select-card';
import { Button, Checkbox } from 'views/components/fonbet/ui';

import { ApplePay } from '../../base/apple-pay';
import { GooglePay } from '../../base/google-pay';

import { CardDetails } from './card-details';
import { type CardFieldsType } from './card-details/type';

const CardForm = () => {
  const { paymentData, isPayment, acquiring } = useAppSelector((state) => state.payment);
  const { locale } = useAppSelector((state) => state.app);
  const { data: cert } = useGetCertQuery(null);
  const [isSaveCard, setIsSaveCard] = useState(false);
  const { fetchSendPayment, isLoading } = usePayment();
  const { addCard, removeCard, cards, selectedCard, setSelectedCard } = useSelectCard('fullCards');
  const [focusName, setFocusName] = useState<keyof CardFieldsType>('pan');
  const { formattedFullAmountWithCurrency, currentAmountForPayment, isValidCurrentAmount } =
    useAmount();
  const { goToReturnUrl } = useMainNavigate();

  const isExpressPayment = useMemo(() => {
    if (paymentData) {
      return paymentData.acquirings.some(
        (item) => item.formType === PAY_METHOD.GOOGLE_PAY || item.formType === PAY_METHOD.APPLE_PAY,
      );
    }

    return false;
  }, [paymentData]);

  const methods = useForm<CardFieldsType>({
    mode: 'onChange',
    defaultValues: isPayment
      ? {
          pan: '',
          month: '',
          year: '',
          cvv: '',
        }
      : { pan: '' },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
    setFocus,
    setError,
    resetField,
    reset,
    watch,
    trigger,
    clearErrors,
  } = methods;

  const month = watch('month');
  const year = watch('year');
  const cardNumber = watch('pan');

  const { t } = useTranslate();
  const { validateCardDate } = useValidate();

  useEffect(() => {
    if (selectedCard.newCard && dirtyFields.month) {
      const validity = `${month}/${year}`;

      const valid = validateCardDate(validity);

      if (typeof valid === 'string') {
        setError('month', {
          message: valid,
        });
        setError('year', {
          message: valid,
        });
      } else {
        clearErrors('month');
        clearErrors('year');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, selectedCard]);

  const fields: (keyof CardFieldsType)[] = ['pan', 'month', 'year', 'cvv'];

  const findIsNextInput = (nameInput: keyof CardFieldsType): keyof CardFieldsType => {
    if (!errors[nameInput] && dirtyFields[nameInput]) {
      const indexField = fields.findIndex((item) => item === nameInput);

      if (indexField !== -1) {
        return findIsNextInput(fields[indexField + 1]);
      }

      return fields[indexField + 1];
    }

    return nameInput;
  };

  useEffect(() => {
    const indexField = fields.findIndex((item) => item === focusName);

    const nextInput = fields[indexField + 1];

    if (nextInput && !errors[focusName] && dirtyFields[focusName]) {
      setFocus(findIsNextInput(nextInput));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.pan, errors.month, errors.year, errors.cvv]);

  useEffect(() => {
    if (!selectedCard.newCard) {
      reset();
      setIsSaveCard(false);
    }

    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard, locale]);

  const onHandleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocusName(e.target.name as keyof CardFieldsType);
  };

  const sendPayment = async (data: CardFieldsType) => {
    if (cert) {
      const cardData = isPayment
        ? {
            cvv: data.cvv,
            validity: `${data.month}/${data.year}`,
            pan: data.pan.replace(/\s/g, ''),
          }
        : {
            pan: data.pan.replace(/\s/g, ''),
          };

      const crypt = await ecryptedWithJwkPublickKey(cert, JSON.stringify(cardData));

      const payData = {
        phone: '',
        email: '',
        description: '',
        ID: paymentData?.ID,
        acquiringID: acquiring?.ID,
        amount: currentAmountForPayment,
        bankBin: cardData.pan ? cardData.pan.slice(0, 6) : null,
        cardData: crypt,
      };

      if (!selectedCard.newCard && selectedCard.crypt && selectedCard.bankBin) {
        payData.cardData = selectedCard.crypt;
        payData.bankBin = selectedCard.bankBin;
      }

      await fetchSendPayment(payData).then(() => {
        if (crypt && isSaveCard) {
          addCard({
            crypt,
            pan: cardData.pan,
          });
        }
      });
    }
  };

  const handleSetIsSaveCard = () => {
    setIsSaveCard(!isSaveCard);
  };

  const renderExpressPaymentBtns = () => {
    return (
      <div className="flex gap-4 bg-white">
        {paymentData?.acquirings.map((item) => {
          if (item.formType === PAY_METHOD.GOOGLE_PAY) {
            return <GooglePay key={item.ID} acquiringID={item.ID} />;
          }

          if (item.formType === PAY_METHOD.APPLE_PAY) {
            return <ApplePay key={item.ID} acquiringID={item.ID} />;
          }

          return null;
        })}
      </div>
    );
  };

  const renderLink = (chunks: ReactNode) => {
    const href = getOfferHrefWithVersion(locale);

    return (
      <a
        href={href}
        rel="noreferrer"
        target="_blank"
        className="underline text-fonbet-blue-100 hover:no-underline"
      >
        {chunks}
      </a>
    );
  };

  const isOnlyOneAcqqiring = useMemo(
    () => isExpressPayment && acquiring,
    [acquiring, isExpressPayment],
  );

  return paymentData ? (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-5 md:gap-6 flex-grow md:flex-grow-0">
        <div className="flex justify-between bg-white">
          <dl className="text-base md:text-lg leading-5 font-normal text-fonbet-gray-200 text-left">
            <dt>{t('1xbet.payment-order')}</dt>
            <dd className="text-fonbet-black font-semibold leading-5 mt-2 md:mt-1.5">
              №{paymentData.ID}
            </dd>
          </dl>
          <dl className="text-base md:text-lg leading-5 font-normal text-fonbet-gray-200 text-right">
            <dt>{t('1xbet.payment-amount')}</dt>
            <dd className="text-fonbet-black font-semibold leading-5 mt-2 md:mt-1.5">
              {formattedFullAmountWithCurrency('KZT')}
            </dd>
          </dl>
        </div>
        {isExpressPayment && <div>{renderExpressPaymentBtns()}</div>}
        {isOnlyOneAcqqiring && (
          <div className="flex items-center justify-between gap-8 text-sz-gray-700 bg-white">
            <div className="max-w-[200px] w-full h-[1px] bg-sz-gray-200" />
            <span>{t('1xbet.or')}</span>
            <div className="max-w-[200px] w-full h-[1px] bg-sz-gray-200" />
          </div>
        )}
        {acquiring && (
          <div className="flex flex-col gap-5 md:gap-6">
            {isOnlyOneAcqqiring && (
              <h3 className="text-lg md:text-xl text-1xbet-black font-semibold leading-4 md:leading-[30px]">
                {t('acquirings-bank-card')}
              </h3>
            )}
            <div className="flex flex-col gap-[22px] md:gap-3 bg-white">
              {cards?.length ? (
                <div>
                  <SelectCard
                    handleDelete={(pan) => removeCard(pan)}
                    cards={cards}
                    selectedCard={selectedCard}
                    setSelectedCard={setSelectedCard}
                    label={t('fonbet.select-card.label')}
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-6">
                <CardDetails
                  cardNumber={cardNumber}
                  control={control}
                  errors={errors}
                  onHandleFocus={onHandleFocus}
                  resetField={resetField}
                  selectedCard={selectedCard}
                  focusName={focusName}
                />

                {selectedCard.newCard && (
                  <div className="flex flex-col gap-3 md:gap-3">
                    <Checkbox
                      name="save-card"
                      label={t('fonbet.save-card')}
                      checked={isSaveCard}
                      onChange={handleSetIsSaveCard}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {acquiring && (
          <>
            <div className="flex flex-col gap-3 md:gap-4 mt-auto">
              <div className="text-[12px] md:text-sm text-center md:hidden block">
                <p>{t('fonbet.terms-of-service', { a: renderLink })}</p>
              </div>
              <Button
                onClick={handleSubmit(sendPayment)}
                disabled={!isValid || !isValidCurrentAmount}
                isLoading={isLoading}
              >
                {isPayment ? t('fonbet.pay') : t('fonbet.payout')}
              </Button>
              <Button
                kind={ButtonKinds.secondary}
                onClick={() => goToReturnUrl(paymentData.returnURL)}
              >
                {t('fonbet.cancel')}
              </Button>
            </div>

            <div className="text-[12px] md:text-sm text-center hidden md:block">
              <p>{t('fonbet.terms-of-service', { a: renderLink })}</p>
            </div>
          </>
        )}
      </form>
    </FormProvider>
  ) : null;
};

export { CardForm };
